import { SlidePlugin } from './SlidePlugin.interface';

/**
 * Interface for custom plugin validators
 */
export interface PluginValidator {
  name: string;
  validate(plugin: SlidePlugin): void;
}

/**
 * Validates plugin version format
 */
export class VersionValidator implements PluginValidator {
  name = 'VersionValidator';

  validate(plugin: SlidePlugin): void {
    const versionPattern = /^\d+\.\d+\.\d+$/;
    if (!versionPattern.test(plugin.version)) {
      throw new Error(
        `Plugin ${plugin.type} has invalid version format "${plugin.version}". Expected format: X.Y.Z`
      );
    }
  }
}

/**
 * Validates plugin capabilities consistency
 */
export class CapabilitiesValidator implements PluginValidator {
  name = 'CapabilitiesValidator';

  validate(plugin: SlidePlugin): void {
    const { capabilities } = plugin;

    // If plugin has options, it must specify min/max options
    if (capabilities.hasOptions) {
      if (typeof capabilities.minOptions !== 'number' || capabilities.minOptions < 0) {
        throw new Error(
          `Plugin ${plugin.type} has options but doesn't specify valid minOptions`
        );
      }
      if (typeof capabilities.maxOptions !== 'number' || capabilities.maxOptions < capabilities.minOptions) {
        throw new Error(
          `Plugin ${plugin.type} has options but doesn't specify valid maxOptions`
        );
      }
    }

    // If plugin is scoreable, it must have scoring methods
    if (capabilities.isScoreable) {
      if (typeof plugin.calculateScore !== 'function') {
        throw new Error(
          `Plugin ${plugin.type} is marked as scoreable but doesn't implement calculateScore`
        );
      }
      if (typeof plugin.getMaxScore !== 'function') {
        throw new Error(
          `Plugin ${plugin.type} is marked as scoreable but doesn't implement getMaxScore`
        );
      }
    }
  }
}

/**
 * Validates plugin type uniqueness and format
 */
export class TypeValidator implements PluginValidator {
  name = 'TypeValidator';
  private registeredTypes = new Set<string>();

  validate(plugin: SlidePlugin): void {
    // Check type format (uppercase with underscores)
    const typePattern = /^[A-Z][A-Z_]*$/;
    if (!typePattern.test(plugin.type)) {
      throw new Error(
        `Plugin type "${plugin.type}" has invalid format. Use uppercase letters and underscores only.`
      );
    }

    // Check for reserved types
    const reservedTypes = ['DEFAULT', 'UNKNOWN', 'ERROR'];
    if (reservedTypes.includes(plugin.type)) {
      throw new Error(
        `Plugin type "${plugin.type}" is reserved and cannot be used`
      );
    }
  }

  reset(): void {
    this.registeredTypes.clear();
  }
}

/**
 * Validates required methods return correct types
 */
export class MethodValidator implements PluginValidator {
  name = 'MethodValidator';

  validate(plugin: SlidePlugin): void {
    // Test createDefault method
    try {
      const defaultSlide = plugin.createDefault();
      if (!defaultSlide || typeof defaultSlide !== 'object') {
        throw new Error('createDefault must return a valid slide object');
      }
      if (defaultSlide.type !== plugin.type) {
        throw new Error('createDefault must return a slide with matching type');
      }
    } catch (error: unknown) {
      throw new Error(
        `Plugin ${plugin.type} createDefault method failed: ${error instanceof Error ? error.message : String(error)}`
      );
    }

    // Test duplicate method with default slide
    try {
      const defaultSlide = plugin.createDefault();
      const duplicated = plugin.duplicate(defaultSlide);
      if (!duplicated || typeof duplicated !== 'object') {
        throw new Error('duplicate must return a valid slide object');
      }
      if (duplicated.type !== plugin.type) {
        throw new Error('duplicate must return a slide with matching type');
      }
      if (duplicated.id === defaultSlide.id) {
        throw new Error('duplicate must generate a new ID');
      }
    } catch (error: unknown) {
      throw new Error(
        `Plugin ${plugin.type} duplicate method failed: ${error instanceof Error ? error.message : String(error)}`
      );
    }

    // Test validation methods
    try {
      const defaultSlide = plugin.createDefault();
      const creationResult = plugin.validateCreation(defaultSlide);
      if (!creationResult || typeof creationResult.valid !== 'boolean') {
        throw new Error('validateCreation must return a ValidationResult');
      }
    } catch (error: unknown) {
      throw new Error(
        `Plugin ${plugin.type} validateCreation method failed: ${error instanceof Error ? error.message : String(error)}`
      );
    }
  }
}

/**
 * Composite validator that runs multiple validators
 */
export class CompositeValidator implements PluginValidator {
  name = 'CompositeValidator';
  private validators: PluginValidator[] = [];

  constructor(validators: PluginValidator[] = []) {
    this.validators = validators;
  }

  addValidator(validator: PluginValidator): void {
    this.validators.push(validator);
  }

  validate(plugin: SlidePlugin): void {
    for (const validator of this.validators) {
      validator.validate(plugin);
    }
  }
}

/**
 * Create default validators
 */
export function createDefaultValidators(): PluginValidator[] {
  return [
    new VersionValidator(),
    new CapabilitiesValidator(),
    new TypeValidator(),
    new MethodValidator(),
  ];
}

/**
 * Create a composite validator with all default validators
 */
export function createDefaultCompositeValidator(): CompositeValidator {
  return new CompositeValidator(createDefaultValidators());
}
